import './useKanbanDataHookConfig';
import { ApolloError, ReactiveVar, useReactiveVar } from '@apollo/client';
import * as _ from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useStudentAdvicedIdCache } from '../../cache/advisor/studentIdReactiveVar';
import { IJobTitleSkillSetVar, jobPreferencesVar } from '../../cache/learner/jobPreferencesReactiveVar';
import {
  IColumnInKanban,
  emptyKanbanDataCache,
  IKanbanCache,
  IKanbanVar,
  ISkillKanban,
  kanbanDataVar,
  StudentSkillStatus,
} from '../../cache/learner/kanbanReactiveVar';
import { recommendedSkillsVar } from '../../cache/learner/recommendedSkillsReactiveVar';
import { SeniorityLevelEnum } from '../../constants/enums/ProficiencyLevelsEnum';
import { SkillStatusEnum } from '../../constants/enums/SkillStatusEnum';
import type { SeniorityLevel } from '../../contexts/SeniorityLevelContext';
import { Mixpanel } from '../../mixpanel/mixpanel';
import { MixpanelComponentsNames } from '../../mixpanel/MixpanelEvents/mixpanelComponentNames';
import { MixpanelEventsEnum } from '../../mixpanel/MixpanelEvents/mixpanelEventsInterface';
import { useMutationCreateOrUpdateStudentSkill } from '../../operations/mutations/student/useCreateOrUpdateStudentSkill/useCreateOrUpdateStudentSkill';
import { useMutationDeleteStudentSkills } from '../../operations/mutations/student/useDeleteStudentSkills/deleteStudentSkills';
import {
  NotificationType,
  useMutationUpdateStudentSkillStatus,
} from '../../operations/mutations/student/useMutationUpdateStudentSkillStatus/useMutationUpdateStudentSkillStatus';
import { useMutationSaveProficiencyLevel } from '../../operations/mutations/student/useSaveProficiencyLevel/saveProficiencyLevel';
import {
  IGetStudentKanbanSkilltrackerAll,
  useLazyGetStudentKanbanSkilltrackerAll,
} from '../../operations/query/students/useGetStudentKanbanSkillTrackerAll/useGetStudentKanbanSkilltrackerAll';
import { decryptor, encryptor } from '../../utils/decryptor';
import { getMixpanelName } from '../../utils/getMixpanelName';
import { getRequiredLevelForSeniority } from '../../utils/getRequiredLevelForSeniority';
import { compareValues } from '../../utils/orderArrayObjectsDynamic';
import { ROLE_ENUM } from '../../views/Auth/Login/interfaces/IGoogleUser';
import StudentSkilltrackerProficiencyLevelFiltersEnum from '../../views/Learner/Skills/components/KanbanBoard/interfaces/StudentSkilltrackerProficiencyLevelFiltersEnum';
import useNotificationsHook, { AddNotificationActionsEnum } from '../notificationsHook/useNotificationsHook';
import useRecommendedSkillsHook from '../recommendedSkillsHook/useRecommendedSkillsHook';
import useLogger from '../useLogger/useLogger';
import userAuth from '../userAuth';
import useStudentRefetch from '../useStudentRefetch';

export interface IDestinationFragEnd {
  droppableId: string;
  index: number;
}

export interface IDragEndParam {
  draggableId: string;
  destination: IDestinationFragEnd;
  source: IDestinationFragEnd;
}

export interface ISkillToAdd {
  id: string;
  name: string;
  type: string;
  originalName?: string;
  translatedType?: string;
}

interface IAddSkillMixpanel {
  recommended: boolean;
  component: string;
}

interface ISkillNotificationOverride {
  title: string;
  description: string;
}

interface IDeleteSkillOptions {
  component: string;
  notification?: ISkillNotificationOverride;
}

interface IGetSkillsAcquiredByStudentInAJobParams {
  skillsInJob: IJobTitleSkillSetVar[];
  qsemEnableProficiencyLevels?: boolean;
  selectedSeniority?: SeniorityLevel | null;
}

interface IGetSkillsAcquiredByStudentInAJob {
  studentAcquiredSkills: IJobTitleSkillSetVar[];
  studentMissingSkills: IJobTitleSkillSetVar[];
}

export interface IKanbanDataHook {
  operations: {
    afterDrag: (result: IDragEndParam) => void;
    changeSkillStatus: (skillId: string, originColumn: IColumnInKanban, destinationColumn: IColumnInKanban) => void;
    addSkill: (
      skill: ISkillToAdd,
      column: IColumnInKanban,
      mixpanelEvents: IAddSkillMixpanel,
      setLoading?: Dispatch<SetStateAction<boolean>>,
      jobTitleId?: string,
    ) => Promise<void>;
    setKanbanData: (data: IGetStudentKanbanSkilltrackerAll[]) => void;
    clearKanbanData: () => void;
    setKanbanDataError: (error: ApolloError | undefined) => void;
    refetchKanbanData: () => void;
    filterKanbanData: (
      skills: ISkillKanban[],
      filterType: string,
      filterJobTitleId: string,
      filterProficiencyLevel: StudentSkilltrackerProficiencyLevelFiltersEnum,
    ) => ISkillKanban[];
    setKanbanDataOrganized: (data: IKanbanCache) => void;
    setFromLocalKanbanDataOrganized: () => boolean;
    deleteSkill: (
      skillId: string,
      skillName: string,
      mixpanelEvents: IDeleteSkillOptions,
      jobIdToUpdate?: string,
    ) => Promise<void>;
    hasKanbanDataAfterFilter: (
      data: IKanbanCache,
      filterType: string,
      filterJobTitleId: string,
      filterProficiencyLevel: StudentSkilltrackerProficiencyLevelFiltersEnum,
    ) => boolean;
    getSkillsAcquiredByStudentInAJob: (
      params: IJobTitleSkillSetVar[] | IGetSkillsAcquiredByStudentInAJobParams,
    ) => IGetSkillsAcquiredByStudentInAJob;
    kanbanData: IKanbanCache;
    updateSkillStatusFromSkillSet: (
      skillId: string,
      skillName: string,
      skillStatus: string,
      originColumn: string,
      notification?: ISkillNotificationOverride,
    ) => Promise<void>;
    updateSkillProficiencyLevel: (
      skillId: string,
      proficiencyLevel: number | undefined,
      setLoading?: Dispatch<SetStateAction<boolean>>,
    ) => Promise<void>;
  };
}
export const emptyCardIdValue = 'empty-card';
const emptyCardValues = [
  {
    name: 'Empty Card',
    originalName: 'Empty Card',
    percentageMatch: 0,
    skillId: emptyCardIdValue,
    status: '',
    translatedType: '',
    type: '',
    validatedSkill: false,
  },
];

const simplifyData = (columnData: IGetStudentKanbanSkilltrackerAll[]): ISkillKanban[] =>
  columnData.map((skill) => ({
    skillId: skill.skillId,
    status: skill.status,
    name: skill.tblSkill.name,
    type: skill.tblSkill.type,
    percentageMatch: skill.percentageMatch ?? 0,
    translatedType: skill.tblSkill.translatedType,
    validatedSkill: skill.validatedSkill ?? false,
    originalName: skill.tblSkill.originalName,
    skillProficiencyLevel: skill.skillProficiencyLevel,
  }));

const useKanbanDataHook = (
  kanbanVarProp: ReactiveVar<IKanbanCache>,
  typeFilterVar: ReactiveVar<string>,
  jobTitleFilterVar: ReactiveVar<string>,
  proficiencyLevelFilterVar: ReactiveVar<StudentSkilltrackerProficiencyLevelFiltersEnum>,
  updateJobPrefsSkillProficiency?: (skillId: string, proficiencyLevel: number | undefined) => void,
): IKanbanDataHook => {
  const kanbanData = kanbanVarProp();
  const typeFilter = typeFilterVar();
  const jobTitleFilter = jobTitleFilterVar();
  const proficiencyLevelFilter = proficiencyLevelFilterVar();
  const jobPreferencesCache = useReactiveVar(jobPreferencesVar);
  const [updateStudentSkillStatus] = useMutationUpdateStudentSkillStatus();
  const [createOrUpdateStudentSkill] = useMutationCreateOrUpdateStudentSkill();
  const [saveProficiencyLevel] = useMutationSaveProficiencyLevel();
  const { pathname, search } = useLocation();
  const { operations: notificationOps } = useNotificationsHook();
  const { operations: loggerOps } = useLogger();
  const { auth } = userAuth();
  const [deleteStudentSkillsMutation] = useMutationDeleteStudentSkills();
  const { updateRecommendedSkills } = useRecommendedSkillsHook(recommendedSkillsVar);
  const [getKanbanData, { data: dataKanban, error: errorKanban }] = useLazyGetStudentKanbanSkilltrackerAll(true);
  const isLearner: boolean = auth?.userRole === ROLE_ENUM.STUDENT;
  const { t } = useTranslation('useKanbanDataHook');
  const studentId: string = auth?.student?.id ?? '';
  const { refetchStudentDataWithoutKanban, incrementPendingOps, decrementPendingOps } = useStudentRefetch();
  const { studentId: advisedStudentId } = useStudentAdvicedIdCache();
  const currentStudentId = advisedStudentId ?? studentId ?? '';

  const queueRefetchStudentData = useMemo(
    () =>
      _.debounce(
        () => {
          refetchStudentDataWithoutKanban?.();
        },
        300,
        { trailing: true },
      ),
    [refetchStudentDataWithoutKanban],
  );

  const queueRefetchKanbanData = useMemo(
    () =>
      _.debounce(
        () => {
          getKanbanData({
            variables: {
              filters: ['specialized', 'human'],
              studentId: currentStudentId,
            },
          });
        },
        300,
        { trailing: true },
      ),
    [getKanbanData, currentStudentId],
  );

  useEffect(
    () => () => {
      // Flush (not cancel) so a refresh queued from a closing modal is not lost.
      queueRefetchStudentData.flush();
      queueRefetchKanbanData.flush();
    },
    [queueRefetchKanbanData, queueRefetchStudentData],
  );

  useEffect(() => {
    if (dataKanban) {
      setKanbanData(dataKanban.getStudentKanbanSkilltrackerAll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKanban]);

  useEffect(() => {
    if (errorKanban) {
      setKanbanDataError(errorKanban);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorKanban]);

  const setLoadingHandler = (isLoading: boolean, setLoading?: Dispatch<SetStateAction<boolean>>): void => {
    if (setLoading) {
      setLoading(isLoading);
    }
  };

  const addSkill = async (
    skill: ISkillToAdd,
    column: IColumnInKanban,
    mixpanelEvents: IAddSkillMixpanel,
    setLoading?: Dispatch<SetStateAction<boolean>>,
    jobTitleId?: string,
  ): Promise<void> => {
    setLoadingHandler(true, setLoading);
    const currentKanbanData = kanbanVarProp();

    const learnerSkillId: string[] = currentKanbanData.data
      .filter((kanbanColumn) => kanbanColumn.id === column)
      .flatMap((learnerKanban) => learnerKanban.items)
      .map(({ skillId }) => skillId);

    if (learnerSkillId.includes(skill.id)) {
      notificationOps.addNotification('danger', t('YOU_ALREADY_HAVE_THIS_SKILL'), t('OOPS'));
      setLoadingHandler(false, setLoading);

      return;
    }

    const columnOrigin = currentKanbanData.data.find((col) => col.items.find((sk) => sk.skillId === skill.id))?.id;
    const columnIndex = currentKanbanData.data.findIndex(({ id }) => id === column);
    const columnOriginIndex = currentKanbanData.data.findIndex(({ id }) => id === columnOrigin);
    const newSkill = {
      skillId: skill.id,
      status: column,
      name: skill.name,
      type: skill.type,
      translatedType: skill.translatedType,
      percentageMatch: 0,
      validatedSkill: false,
      skillProficiencyLevel: column === StudentSkillStatus.COMPLETED ? 1 : undefined,
    };

    const copy = structuredClone(currentKanbanData);

    if (copy.data[columnOriginIndex]?.items) {
      copy.data[columnOriginIndex].items = copy.data[columnOriginIndex].items.filter(
        (item) => item.skillId !== skill.id,
      );
    }

    copy.data[columnIndex].items.push(newSkill);

    setKanbanDataOrganized(copy);
    incrementPendingOps();
    try {
      const response = await createOrUpdateStudentSkill({
        variables: {
          skillStatus: column,
          skillId: skill.id,
          studentId: currentStudentId,
          fromStatus: columnOrigin,
          jobTitleId,
        },
      });

      decrementPendingOps();

      if (columnOrigin === SkillStatusEnum.COMPLETED || column === SkillStatusEnum.COMPLETED) {
        queueRefetchStudentData();
      }
      const responseNotification = response.data?.createOrUpdateStudentSkill?.studentNotification;

      setLoadingHandler(false, setLoading);
      notificationOps.addSkillNotification({
        action: AddNotificationActionsEnum.ADD,
        data: {
          skillId: skill.id,
          skillName: skill.name,
          toStatus: column,
          fromStatus: columnOrigin,
          databaseNotification: responseNotification
            ? {
                id: responseNotification.id,
                date: responseNotification.date,
                type: responseNotification.type as NotificationType,
                skillName: skill.name,
              }
            : undefined,
        },
      });
    } catch (err: unknown) {
      decrementPendingOps();
      loggerOps
        .writeLogger({
          project: 'front-end',
          source: 'useKanbanDataHook',
          func: 'addSkill',
          error: JSON.stringify(err),
          level: 'error',
        })
        .catch(() => null);
      notificationOps.addNotification('danger', t('SOMETHING_WENT_WRONG'), t('ERROR'));
      refetchKanbanData();
      setLoadingHandler(false, setLoading);

      return;
    }

    Mixpanel.track(
      {
        action: MixpanelEventsEnum.ADD_SKILL,
        data: {
          screen: getMixpanelName(pathname.concat(search)),
          component: mixpanelEvents?.component ?? '',
          action: 'click',
          skillName: skill.originalName ?? skill.name,
          skillStatus: column,
          skillType: skill.type,
          recommended: mixpanelEvents?.recommended ?? false,
        },
      },
      isLearner,
    );
    updateRecommendedSkills(skill.id);
  };

  const reorderSkill = (columnData: IKanbanVar, originIndex: number, destinationIndex: number): ISkillKanban[] => {
    const columnItems = columnData.items;
    const itemSelected = filterKanbanData(columnItems, typeFilter, jobTitleFilter, proficiencyLevelFilter)[originIndex];
    const indexToMove = columnItems.findIndex(({ skillId }) => itemSelected.skillId === skillId);

    if (indexToMove < 0) {
      return columnItems;
    }

    const [itemToMove] = columnItems.splice(indexToMove, 1);

    if (destinationIndex > 0) {
      const itemBelow = filterKanbanData(columnItems, typeFilter, jobTitleFilter, proficiencyLevelFilter)[
        destinationIndex - 1
      ];
      const indexDestination = columnItems.findIndex(({ skillId }) => itemBelow.skillId === skillId) + 1;

      columnItems.splice(indexDestination, 0, itemToMove);
    } else {
      columnItems.splice(0, 0, itemToMove);
    }

    return columnItems;
  };

  const updateSkillStatus = async ({
    skillId,
    skillName,
    skillStatus,
    originColumn,
    skillType,
    originalName,
  }: {
    skillId: string;
    skillName: string;
    skillStatus: string;
    originColumn: string;
    skillType: string;
    originalName?: string;
  }): Promise<void> => {
    incrementPendingOps();
    try {
      const response = await updateStudentSkillStatus({
        variables: {
          skillStatus,
          skillId,
          studentId: currentStudentId,
          fromStatus: originColumn,
        },
      });

      decrementPendingOps();

      if (skillStatus === SkillStatusEnum.COMPLETED || originColumn === SkillStatusEnum.COMPLETED) {
        queueRefetchStudentData();
      }
      const responseNotification = response.data?.updateStudentSkillStatus.studentNotification;

      notificationOps.addSkillNotification({
        action: AddNotificationActionsEnum.MODIFY,
        data: {
          skillId,
          skillName,
          fromStatus: originColumn,
          toStatus: skillStatus,
          databaseNotification: responseNotification
            ? {
                id: responseNotification.id,
                date: responseNotification.date,
                type: responseNotification.type as NotificationType,
                skillName,
              }
            : undefined,
        },
      });
    } catch (err: unknown) {
      decrementPendingOps();
      loggerOps
        .writeLogger({
          project: 'front-end',
          source: 'useKanbanDataHook',
          func: 'updateSkillStatus',
          error: JSON.stringify(err),
          level: 'error',
        })
        .catch(() => null);
      notificationOps.addNotification('danger', t('SOMETHING_WENT_WRONG'), t('ERROR'));
      refetchKanbanData();

      return;
    }
    Mixpanel.track(
      {
        action: MixpanelEventsEnum.MODIFY_SKILL,
        data: {
          skillName: originalName ?? skillName,
          skillStatus,
          skillType,
          fromStatus: originColumn,
          screen: getMixpanelName(`${pathname}${search}`),
          component: MixpanelComponentsNames.KANBAN_BOARD,
          action: 'click',
        },
      },
      isLearner,
    );
  };

  const changeSkill = (
    dataCopy: IKanbanCache,
    originColumnIndex: number,
    destinationColumnIndex: number,
    result: IDragEndParam,
  ): void => {
    const { source, destination } = result;
    const copyOfData = structuredClone(dataCopy);
    const originSkills = dataCopy.data[originColumnIndex].items;
    const destinationSkills = dataCopy.data[destinationColumnIndex].items;
    const destinationColumn = dataCopy.data[destinationColumnIndex];
    const originColumn = dataCopy.data[originColumnIndex];

    const itemSelected = filterKanbanData(originSkills, typeFilter, jobTitleFilter, proficiencyLevelFilter)[
      source.index
    ];
    const indexItemMove = originSkills.findIndex(({ skillId }) => itemSelected.skillId === skillId);

    if (indexItemMove < 0) {
      return;
    }

    const [itemToMove] = originSkills.splice(indexItemMove, 1);

    itemToMove.status = destinationColumn.id;

    if (destinationColumn.id === StudentSkillStatus.COMPLETED) {
      itemToMove.skillProficiencyLevel = Math.max(itemToMove.skillProficiencyLevel ?? 0, 1);
    } else if (originColumn.id === StudentSkillStatus.COMPLETED) {
      itemToMove.skillProficiencyLevel = undefined;
    }

    if (destination.index > 0) {
      const itemBelow = filterKanbanData(destinationSkills, typeFilter, jobTitleFilter, proficiencyLevelFilter)[
        destination.index - 1
      ];
      const indexDestination = destinationSkills.findIndex(({ skillId }) => itemBelow.skillId === skillId) + 1;

      destinationSkills.splice(indexDestination, 0, itemToMove);
    } else {
      destinationSkills.splice(0, 0, itemToMove);
    }

    copyOfData.data[originColumnIndex].items = originSkills;
    copyOfData.data[destinationColumnIndex].items = destinationSkills;

    setKanbanDataOrganized(copyOfData);

    updateSkillStatus({
      skillId: itemToMove.skillId,
      skillName: itemToMove.name,
      skillStatus: destinationColumn.id,
      originColumn: originColumn.id,
      skillType: itemToMove.type,
      originalName: itemToMove.originalName,
    }).catch(() => null);
  };

  const changeSkillStatus = (
    skillId: string,
    originColumn: IColumnInKanban,
    destinationColumn: IColumnInKanban,
  ): void => {
    const currentKanbanData = kanbanVarProp();

    if (!currentKanbanData.data?.length) {
      return;
    }

    if (originColumn === destinationColumn) {
      return;
    }

    const originColumnIndex = currentKanbanData.data.findIndex(({ id }) => id === originColumn);
    const destinationColumnIndex = currentKanbanData.data.findIndex(({ id }) => id === destinationColumn);

    if (originColumnIndex < 0 || destinationColumnIndex < 0) {
      return;
    }

    const copyOfData = structuredClone(currentKanbanData);
    const originItems = copyOfData.data[originColumnIndex].items;
    const destinationItems = copyOfData.data[destinationColumnIndex].items;
    const indexItemMove = originItems.findIndex((item) => item.skillId === skillId);

    if (indexItemMove < 0) {
      return;
    }

    const [itemToMove] = originItems.splice(indexItemMove, 1);

    itemToMove.status = destinationColumn;

    if (destinationColumn === StudentSkillStatus.COMPLETED) {
      itemToMove.skillProficiencyLevel = Math.max(itemToMove.skillProficiencyLevel ?? 0, 1);
    } else if (originColumn === StudentSkillStatus.COMPLETED) {
      itemToMove.skillProficiencyLevel = undefined;
    }

    const indexExistingInDestination = destinationItems.findIndex((item) => item.skillId === skillId);

    if (indexExistingInDestination >= 0) {
      destinationItems.splice(indexExistingInDestination, 1);
    }

    destinationItems.splice(0, 0, itemToMove);

    copyOfData.data[originColumnIndex].items = originItems;
    copyOfData.data[destinationColumnIndex].items = destinationItems;

    setKanbanDataOrganized(copyOfData);

    updateSkillStatusFromSkillSet(skillId, itemToMove.name, destinationColumn, originColumn).catch(() => null);
  };

  const moveSkill = (result: IDragEndParam): void => {
    const currentKanbanData = kanbanVarProp();

    if (!currentKanbanData.data) {
      return;
    }
    const { source, destination } = result;
    const originColumnIndex: number = currentKanbanData.data.findIndex(({ id }) => id === source.droppableId);
    const destinationColumnIndex: number = currentKanbanData.data.findIndex(({ id }) => id === destination.droppableId);
    const destinationColumn = currentKanbanData.data[destinationColumnIndex];
    const copyOfData = structuredClone(currentKanbanData);

    if (originColumnIndex === destinationColumnIndex) {
      copyOfData.data[destinationColumnIndex].items = reorderSkill(destinationColumn, source.index, destination.index);

      setKanbanDataOrganized(copyOfData);
    } else {
      changeSkill(currentKanbanData, originColumnIndex, destinationColumnIndex, result);
    }
  };

  const afterDrag = (result: IDragEndParam): void => {
    const { destination } = result;

    if (!destination) {
      return;
    }
    moveSkill(result);
  };

  const clearKanbanData = (): void => {
    setKanbanDataOrganized(emptyKanbanDataCache);
  };

  const setKanbanDataError = (error: ApolloError | undefined): void => {
    const copyOfData = structuredClone(kanbanVarProp());

    copyOfData.error = error;
    setKanbanDataOrganized(copyOfData);
  };

  const setKanbanData = (data: IGetStudentKanbanSkilltrackerAll[]): void => {
    const backlogSkills = data
      .filter(({ status }) => status === 'backlog')
      .sort(compareValues({ key: 'percentageMatch', order: 'desc' }));
    const learningSkills = data
      .filter(({ status }) => status === 'learning')
      .sort(compareValues({ key: 'percentageMatch', order: 'desc' }));
    const completedSkills = data
      .filter(({ status }) => status === 'completed')
      .sort(compareValues({ key: 'percentageMatch', order: 'desc' }));

    const filteredData = [
      {
        id: StudentSkillStatus.TOLEARN,
        items: simplifyData(backlogSkills),
        title: t('TO_LEARN'),
      },
      {
        id: StudentSkillStatus.LEARNING,
        items: simplifyData(learningSkills),
        title: t('LEARNING'),
      },
      {
        id: StudentSkillStatus.COMPLETED,
        items: simplifyData(completedSkills),
        title: t('COMPLETED'),
      },
    ];

    const kanbanVar = {
      error: kanbanVarProp().error,
      data: filteredData,
    };

    kanbanDataVar(kanbanVar);
    const encryptedData = encryptor(JSON.stringify(filteredData));

    if (!encryptedData) {
      return;
    }

    sessionStorage.setItem('kanbanData', encryptedData);
  };

  const refetchKanbanData = (): void => {
    getKanbanData({
      variables: {
        filters: ['specialized', 'human'],
        studentId: currentStudentId,
      },
    });
  };

  const filterKanbanData = (
    skills: ISkillKanban[],
    filterType: string,
    filterJobTitleId: string,
    filterProficiencyLevel: StudentSkilltrackerProficiencyLevelFiltersEnum,
  ): ISkillKanban[] => {
    const skillsId: string[] = [];

    if (skills.length === 0) {
      return emptyCardValues;
    }

    const shouldFilterByProficiency = filterProficiencyLevel !== StudentSkilltrackerProficiencyLevelFiltersEnum.ALL;
    const proficiencyLevelNumber = (() => {
      switch (filterProficiencyLevel) {
        case StudentSkilltrackerProficiencyLevelFiltersEnum.ENTRY_LEVEL:
          return 1;

        case StudentSkilltrackerProficiencyLevelFiltersEnum.MID_LEVEL:
          return 2;

        case StudentSkilltrackerProficiencyLevelFiltersEnum.SENIOR_LEVEL:
          return 3;

        default:
          return undefined;
      }
    })();

    const applyProficiencyFilter = (skillsToFilter: ISkillKanban[]): ISkillKanban[] => {
      if (!shouldFilterByProficiency || !proficiencyLevelNumber) {
        return skillsToFilter;
      }

      return skillsToFilter.filter((skill) => skill.skillProficiencyLevel === proficiencyLevelNumber);
    };

    jobPreferencesCache.data
      .filter((jobPreference) => jobPreference.jobTitleId === filterJobTitleId)
      .forEach((jobTitle) => {
        jobTitle.jobTitleSkillSet.forEach((skillSet) => {
          if (!skillsId.includes(skillSet.skillId)) {
            skillsId.push(skillSet.skillId);
          }
        });
      });
    if (filterType === 'all' && filterJobTitleId === 'all') {
      return applyProficiencyFilter(skills);
    }
    if (filterType === 'all' && filterJobTitleId !== 'all') {
      return applyProficiencyFilter(skills.filter((skill) => skillsId.includes(skill.skillId)));
    }
    if (filterType !== 'all' && filterJobTitleId === 'all') {
      return applyProficiencyFilter(skills.filter(({ type }) => type === filterType.toLocaleLowerCase()));
    }

    return applyProficiencyFilter(
      skills
        .filter(({ type }) => type === filterType.toLocaleLowerCase())
        .filter(({ skillId }) => skillsId.includes(skillId)),
    );
  };

  const setKanbanDataOrganized = (data: IKanbanCache): void => {
    kanbanDataVar(data);
    const encryptedData = encryptor(JSON.stringify(data.data));

    if (!encryptedData) {
      return;
    }

    sessionStorage.setItem('kanbanData', encryptedData);
  };

  const setFromLocalKanbanDataOrganized = (): boolean => {
    const dataLocal = sessionStorage.getItem('kanbanData');
    const decryptedData = decryptor(dataLocal ?? '');

    if (!decryptedData) {
      return false;
    }

    let storedColumns: unknown;

    try {
      storedColumns = JSON.parse(decryptedData);
    } catch {
      return false;
    }

    // A session that stored a serialized null would hand it straight to the board, which
    // expects an array of columns. Falling back to false makes the caller refetch instead.
    if (!Array.isArray(storedColumns)) {
      return false;
    }

    kanbanDataVar({ error: kanbanVarProp().error, data: storedColumns as IKanbanVar[] });

    return true;
  };

  const deleteSkill = async (
    skillId: string,
    skillName: string,
    mixpanelEvents: IDeleteSkillOptions,
    jobIdToUpdate?: string,
  ): Promise<void> => {
    const currentKanbanData = kanbanVarProp();

    const copyOfData = structuredClone(currentKanbanData);

    const columnModify = copyOfData.data.find((column) => column.items.find((skill) => skill.skillId === skillId));

    if (!columnModify) {
      return;
    }

    const skillToDelete = columnModify.items.find((skill) => skill.skillId === skillId);
    const columnToChange: IKanbanVar = {
      id: columnModify.id,
      title: columnModify.title,
      items: columnModify.items.filter((skill) => skill.skillId !== skillId) || [],
    };

    const indexColumn = copyOfData.data.findIndex((column) => column.items.find((skill) => skill.skillId === skillId));

    copyOfData.data.splice(indexColumn, 1, columnToChange);

    if (columnToChange?.id) {
      incrementPendingOps();
      try {
        setKanbanDataOrganized(copyOfData);

        const response = await deleteStudentSkillsMutation({
          variables: {
            studentId: currentStudentId,
            skillIdList: [skillId],
            jobTitleId: jobIdToUpdate,
          },
        });

        decrementPendingOps();
        queueRefetchStudentData();

        const responseNotification = response.data?.deleteStudentSkills.studentNotification;

        if (mixpanelEvents.notification) {
          notificationOps.addNotification(
            'removed',
            mixpanelEvents.notification.description,
            mixpanelEvents.notification.title,
          );
        } else {
          notificationOps.addSkillNotification({
            action: AddNotificationActionsEnum.DELETE,
            data: {
              skillId,
              skillName: skillToDelete?.name ?? skillName ?? '',
              fromStatus: columnModify.id,
              databaseNotification: responseNotification
                ? {
                    id: responseNotification.id,
                    date: responseNotification.date,
                    type: responseNotification.type as NotificationType,
                    skillName: skillToDelete?.name ?? skillName ?? '',
                  }
                : undefined,
            },
          });
        }
      } catch (err: unknown) {
        decrementPendingOps();
        loggerOps
          .writeLogger({
            project: 'front-end',
            source: 'useKanbanDataHook',
            func: 'deleteSkill',
            error: JSON.stringify(err),
            level: 'error',
          })
          .catch(() => null);
        notificationOps.addNotification('danger', t('SOMETHING_WENT_WRONG'), t('ERROR'));
        refetchKanbanData();
      }
    } else {
      notificationOps.addNotification('danger', t('SOMETHING_WENT_WRONG_PLAIN'), t('OOPS'));
    }

    Mixpanel.track(
      {
        action: MixpanelEventsEnum.DELETE_SKILL,
        data: {
          skillName: skillToDelete?.originalName ?? '',
          skillStatus: skillToDelete?.status ?? '',
          skillType: skillToDelete?.type ?? '',
          screen: getMixpanelName(`${pathname}${search}`),
          action: 'click',
          component: mixpanelEvents?.component ?? '',
        },
      },
      isLearner,
    );
  };

  const hasKanbanDataAfterFilter = (
    data: IKanbanCache,
    filterType: string,
    filterJobTitleId: string,
    filterProficiencyLevel: StudentSkilltrackerProficiencyLevelFiltersEnum,
  ): boolean => {
    const skillsNumber = data.data.reduce(
      (accumulatedLength, currentData) =>
        accumulatedLength +
        filterKanbanData(currentData.items, filterType, filterJobTitleId, filterProficiencyLevel).length,
      0,
    );

    return skillsNumber > 0;
  };

  const getSkillsAcquiredByStudentInAJob = (
    params: IJobTitleSkillSetVar[] | IGetSkillsAcquiredByStudentInAJobParams,
  ): IGetSkillsAcquiredByStudentInAJob => {
    const defaultSeniorityLevel: SeniorityLevel = SeniorityLevelEnum.JUNIOR;
    const isNewSignature = !Array.isArray(params) && 'skillsInJob' in params;
    const skillsInJob = isNewSignature ? params.skillsInJob : params;
    const qsemEnableProficiencyLevels = isNewSignature ? params.qsemEnableProficiencyLevels ?? false : false;
    const selectedSeniority: SeniorityLevel = isNewSignature
      ? params.selectedSeniority ?? defaultSeniorityLevel
      : defaultSeniorityLevel;

    const studentSkillsAcquired = kanbanVarProp().data.find((column) => column.id === 'completed')?.items ?? [];

    const studentAcquiredSkills: IJobTitleSkillSetVar[] = [];
    const studentMissingSkills: IJobTitleSkillSetVar[] = [];

    skillsInJob.forEach((skill) => {
      let itemMatched: ISkillKanban | undefined;

      if (qsemEnableProficiencyLevels) {
        const requiredLevel = getRequiredLevelForSeniority(skill.seniorityRequirements, selectedSeniority);

        itemMatched = studentSkillsAcquired.find(
          (studentSkill) =>
            studentSkill.skillId === skill.skillId &&
            studentSkill.skillProficiencyLevel !== undefined &&
            studentSkill.skillProficiencyLevel !== null &&
            studentSkill.skillProficiencyLevel >= requiredLevel,
        );
      } else {
        // Lógica actual (solo por skillId)
        itemMatched = studentSkillsAcquired.find((studentSkill) => studentSkill.skillId === skill.skillId);
      }

      if (itemMatched) {
        studentAcquiredSkills.push(skill);
      } else {
        studentMissingSkills.push(skill);
      }
    });

    return {
      studentAcquiredSkills,
      studentMissingSkills,
    };
  };

  const updateSkillStatusFromSkillSet = async (
    skillId: string,
    skillName: string,
    skillStatus: string,
    originColumn: string,
    notification?: ISkillNotificationOverride,
  ): Promise<void> => {
    incrementPendingOps();
    try {
      await updateStudentSkillStatus({
        variables: {
          skillStatus,
          skillId,
          studentId: currentStudentId,
          fromStatus: originColumn,
        },
      });

      decrementPendingOps();

      if (skillStatus === SkillStatusEnum.COMPLETED || originColumn === SkillStatusEnum.COMPLETED) {
        queueRefetchStudentData();

        if (originColumn === SkillStatusEnum.COMPLETED && skillStatus === SkillStatusEnum.BACKLOG) {
          if (notification) {
            notificationOps.addNotification('removed', notification.description, notification.title);
          } else {
            notificationOps.addSkillNotification({
              action: AddNotificationActionsEnum.DELETE,
              data: {
                skillId,
                skillName: skillName ?? '',
                fromStatus: originColumn,
              },
            });
          }
        }

        return;
      }

      queueRefetchKanbanData();
    } catch (err: unknown) {
      decrementPendingOps();
      loggerOps
        .writeLogger({
          project: 'front-end',
          source: 'useKanbanDataHook',
          func: 'updateSkillStatusFromSkillSet',
          error: JSON.stringify(err),
          level: 'error',
        })
        .catch(() => null);
    }
  };

  const updateSkillProficiencyLevel = async (
    skillId: string,
    proficiencyLevel: number | undefined,
    setLoading?: Dispatch<SetStateAction<boolean>>,
  ): Promise<void> => {
    setLoadingHandler(true, setLoading);
    const currentKanbanData = kanbanVarProp();

    const copy = structuredClone(currentKanbanData);
    let skillFound = false;

    copy.data.forEach((column) => {
      const skill = column.items.find((item) => item.skillId === skillId);

      if (skill) {
        skill.skillProficiencyLevel = proficiencyLevel;
        skillFound = true;
      }
    });

    if (skillFound) {
      setKanbanDataOrganized(copy);
      updateJobPrefsSkillProficiency?.(skillId, proficiencyLevel);
    } else {
      setLoadingHandler(false, setLoading);
    }

    try {
      await saveProficiencyLevel({
        variables: {
          skillId,
          studentId: currentStudentId,
          currentProficiencyLevel: proficiencyLevel ?? 0,
          shouldReset: proficiencyLevel === undefined,
        },
      });
      queueRefetchStudentData();
      setLoadingHandler(false, setLoading);
    } catch (err: unknown) {
      loggerOps
        .writeLogger({
          project: 'front-end',
          source: 'useKanbanDataHook',
          func: 'updateSkillProficiencyLevel',
          error: JSON.stringify(err),
          level: 'error',
        })
        .catch(() => null);
      notificationOps.addNotification('danger', t('SOMETHING_WENT_WRONG'), t('ERROR'));
      queueRefetchStudentData();
      setLoadingHandler(false, setLoading);
    }
  };

  return {
    operations: {
      afterDrag,
      changeSkillStatus,
      addSkill,
      setKanbanData,
      clearKanbanData,
      setKanbanDataError,
      refetchKanbanData,
      filterKanbanData,
      setKanbanDataOrganized,
      setFromLocalKanbanDataOrganized,
      deleteSkill,
      hasKanbanDataAfterFilter,
      getSkillsAcquiredByStudentInAJob,
      kanbanData,
      updateSkillStatusFromSkillSet,
      updateSkillProficiencyLevel,
    },
  };
};

export default useKanbanDataHook;
